import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { useLeavePolicyTemplateApi } from './config/index';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';

const Container = styled.div``;

// superAdmin şablon yönetimi (ülke bazlı izin politikası kalıpları). Menü +
// rota holiday-calendars ile AYNI desende superAdmin'e kilitli (bkz.
// MenuConfig.js, routes/index.js) — bu sayfa gördüğü herkes zaten superAdmin
// dalından geçmiştir, holiday-calendars'ta olduğu gibi ekstra bir rol kontrolü
// tekrarlanmaz.
//
// Form tek bir GenericFormDrawer'da İKİ bölüm taşır: şablon alanları (ad/ülke/
// açıklama) + tür listesi (LeavePolicyTemplateVariationsEditor, 'leavePolicyTemplateVariations'
// alan tipi). Ayrı bir detay sayfası/sekmesi KURULMADI — backend tek bir PUT/POST
// çağrısında tüm şablonu (Variations dahil) aldığı için (LeavePolicyTemplateController'da
// LeavePolicy'nin aksine variation-bazlı ayrı bir uç yok), iki ayrı ekran arasında
// senkronize edilecek iki farklı persist akışı olmazdı — tek form, tek kaydet.
//
// GetAll parametre almaz, tüm liste tek seferde döner — DataTable roles/comp-cycles/
// calibration-sessions ile AYNI desende `serverSide={false}` alır (client-side
// filtre/sıralama/sayfalama); filterList/onFilterConfirm/onTableChanged o
// desende hiç kullanılmaz (yalnız serverSide=true'nun refetch döngüsü için var).
export default function LeavePolicyTemplatesPage() {
  const { t } = useTranslation();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);

  const { objectSchema, fetching, columns: baseColumns, data, getOne, create, update, _delete } = useLeavePolicyTemplateApi();

  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  // GetAll'un özet satırı Variations'ı taşımaz (yalnız VariationCount) — tam
  // kayıt GetById'den çekilmeden form açılırsa tür listesi sessizce boşalır.
  const handleEdit = useCallback(async (row) => {
    const full = await getOne(row.id);
    if (full) {
      setSelectedRow(full);
      setDrawerOpen(true);
    }
  }, [getOne]);

  const handleSave = useCallback(async (formData) => {
    const success = selectedRow
      ? await update(selectedRow.id, formData)
      : await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [selectedRow, create, update]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.name });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: `${ids.length} ${t('leavePolicyTemplate.title').toLowerCase()}` });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  const actionOptions = useMemo(() => [
    { type: 'edit', label: t('leavePolicyTemplate.edit'), onClick: handleEdit },
    { type: 'divider' },
    { type: 'delete', label: t('leavePolicyTemplate.delete'), onClick: handleDelete },
  ], [t, handleEdit, handleDelete]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <OptionsMenu actions={actionOptions} row={row} />;
        },
      },
    },
  ], [baseColumns, actionOptions, data]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? t('leavePolicyTemplate.edit') : t('leavePolicyTemplate.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('leavePolicyTemplate.deleteTitle')}
        message={t('leavePolicyTemplate.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      {useMemo(() => (
        <DataTable
          title={t('leavePolicyTemplate.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={data.length}
          pageIndex={0}
          pageSize={data.length || 10}
          canCreateNewRecord={true}
          onNewButtonClick={handleNewButtonClick}
          onRowsDelete={handleRowsDelete}
          serverSide={false}
        />
      ), [
        t, fetching, columns, data, handleNewButtonClick, handleRowsDelete,
      ])}
    </Container>
  );
}
